import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Save, BellRing } from "lucide-react";
import { toast } from "sonner";

// Admin: when each of the 5 auto reminders is sent (days after the proposal was sent)
// and whether each is enabled. The wording lives in the Reminder 1–5 email templates below.
export default function ReminderScheduleCard({ canEdit }) {
  const [items, setItems] = useState([]);
  const load = () => api.get("/reminder-schedule").then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const upd = (n, patch) => setItems((xs) => xs.map((x) => (x.n === n ? { ...x, ...patch } : x)));
  const save = async () => {
    try { const { data } = await api.put("/reminder-schedule", { items }); setItems(data.items || []); toast.success("Reminder schedule saved"); }
    catch { toast.error("Could not save schedule"); }
  };
  return (
    <Card className="p-6 rounded-md space-y-3" data-testid="reminder-schedule-card">
      <div className="flex items-center gap-2">
        <BellRing className="w-4 h-4 text-cass-teal" />
        <h4 className="font-alt font-semibold text-cass-navy">Reminder schedule</h4>
      </div>
      <p className="text-sm text-cass-slate">Set how many calendar days after a proposal is <b>sent</b> each reminder goes out to anyone who hasn't signed. Reminders stop automatically once a proposal is accepted, rejected, expired or withdrawn. Edit the wording in the Reminder 1–5 templates below.</p>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.n} className="flex items-center gap-3 border border-border rounded-md px-3 py-2" data-testid={`reminder-row-${it.n}`}>
            <span className="grid place-items-center w-6 h-6 rounded-full bg-cass-teal text-white text-xs font-semibold shrink-0">{it.n}</span>
            <span className="text-sm text-cass-navy w-24">Reminder {it.n}</span>
            <div className="flex items-center gap-2">
              <Input type="number" min={0} className="w-20 h-8" value={it.days} disabled={!canEdit}
                onChange={(e) => upd(it.n, { days: Number(e.target.value) })} data-testid={`reminder-days-${it.n}`} />
              <span className="text-xs text-cass-slate">days after sent</span>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Switch checked={it.enabled} disabled={!canEdit} onCheckedChange={(v) => upd(it.n, { enabled: !!v })} data-testid={`reminder-enabled-${it.n}`} />
              <span className="text-xs text-cass-slate w-12">{it.enabled ? "On" : "Off"}</span>
            </div>
          </div>
        ))}
      </div>
      {canEdit && <Button className="bg-cass-navy text-white" onClick={save} data-testid="reminder-schedule-save"><Save className="w-4 h-4 mr-1" />Save schedule</Button>}
    </Card>
  );
}
